import type { ComponentType } from '@angular/cdk/portal';
import type { Injector, TemplateRef, ViewContainerRef } from '@angular/core';
import {
  Compiler,
  ErrorHandler,
  Inject,
  Injectable,
  Optional,
} from '@angular/core';
import { Location } from '@freelancer/location';
import { SwipeNavigationService } from '@freelancer/pwa';
import { retryBackoff } from 'backoff-rxjs';
import * as Rx from 'rxjs';
import { catchError, retryWhen, switchMap, tap } from 'rxjs/operators';
import { ModalRef } from './modal-ref';
import type { ModalName } from './modal-types';
import type { LazyModal } from './modal.config';
import { MODALS, TESTING_MODAL_CLOSE_ANIMATION_DISABLED } from './modal.config';
import type {
  ModalCloseConfig,
  ModalConfig,
  ModalLoadingStatus,
} from './modal.types';

/**
 * Custom injector to be used when providing custom
 * injection tokens to components inside a portal.
 */
export class PortalInjector implements Injector {
  constructor(
    private _parentInjector: Injector,
    private _customTokens: WeakMap<any, any>,
  ) {}

  get(token: any, notFoundValue?: any): any {
    const value = this._customTokens.get(token);

    if (typeof value !== 'undefined') {
      return value;
    }

    return this._parentInjector.get<any>(token, notFoundValue);
  }
}

@Injectable({ providedIn: 'root' })
export class ModalService {
  private _openStreamSubject$ = new Rx.Subject<any>();
  openStream$ = this._openStreamSubject$.asObservable();
  private _statusStreamSubject$ = new Rx.Subject<ModalLoadingStatus>();
  statusStream$ = this._statusStreamSubject$.asObservable();
  private _beforeCloseStreamSubject$ = new Rx.Subject<void>();
  beforeCloseStream$ = this._beforeCloseStreamSubject$.asObservable();
  private retryStreamSubject$: Rx.Subject<undefined>;
  private _titleStreamSubject$ = new Rx.BehaviorSubject<
    TemplateRef<any> | undefined
  >(undefined);
  titleStream$ = this._titleStreamSubject$.asObservable();

  // rendering container
  private injector: Injector;
  private viewContainerRef: ViewContainerRef;
  private openModalRef?: ModalRef<any>;
  private modalConfig: ModalConfig;

  private allModals: { [k: string]: LazyModal };

  constructor(
    // eslint-disable-next-line deprecation/deprecation
    private compiler: Compiler,
    @Inject(MODALS)
    private readonly providedModals: { [k: string]: LazyModal }[],
    private errorHandler: ErrorHandler,
    private location: Location,
    private swipeNavigation: SwipeNavigationService,
    /** This should only be injected in UI tests */
    @Optional()
    @Inject(TESTING_MODAL_CLOSE_ANIMATION_DISABLED)
    private readonly testingCloseAnimationDisabled?: boolean,
  ) {}

  close(config: ModalCloseConfig = {}) {
    if (this.openModalRef) {
      this.openModalRef.close(undefined, config);
      this.swipeNavigation.enableSwipeGestures(true);
    }
  }

  /**
   * Destroys the component after running the hide animation in the modal component
   * NOTE: This is private and should not be used by app code, call .close() instead
   */
  _destroy() {
    if (this.openModalRef) {
      this.openModalRef._destroy();
      this.openModalRef = undefined;
    }
    if (this.retryStreamSubject$) {
      this.retryStreamSubject$.complete();
    }
  }

  open(component: ModalName, modalConfig: ModalConfig): ModalRef<any> {
    this.modalConfig = modalConfig;

    const modalRef = new ModalRef(this._openStreamSubject$, this.location);
    this._titleStreamSubject$.next(undefined);

    // close any open modals and wait for them to close
    let ready$ = Rx.of(true);
    if (this.openModalRef) {
      ready$ = Rx.from(this.openModalRef.afterClosed());
      this.close();
    }

    this.retryStreamSubject$ = new Rx.Subject<undefined>();
    // asynchronously set the componentRef in the modal
    // we return immediately to make the API nicer.
    ready$
      .pipe(
        tap(() => {
          // mark modal as open after closing previous one
          this._openStreamSubject$.next(
            modalConfig.showBackdrop !== undefined
              ? modalConfig.showBackdrop
              : true,
          );
          this.openModalRef = modalRef;
          this.swipeNavigation.enableSwipeGestures(false);
          modalRef.open();
          this.openModalRef._beforeClose().then(_ => {
            if (this.testingCloseAnimationDisabled) {
              // Immediately destroys the open modal rather than waiting for
              // the `animationDone` callback. Animations are currently broken
              // in UI tests, specifically the timing of animation callbacks
              // being later than expected.
              this._destroy();
            } else {
              this._beforeCloseStreamSubject$.next();
            }
          });
        }),
        // reset the loading status
        tap(() => this._statusStreamSubject$.next({ ready: false })),
        switchMap(() => this.getModalNgModule(component)),
        // compile the module into an ngModuleFactory
        switchMap(async ({ modalModule, componentType }) => ({
          compiledModule: await this.compiler.compileModuleAsync(modalModule),
          componentType,
        })),
        // on failure retry twice with exponential backoff
        retryBackoff({
          initialInterval: 200,
          maxRetries: 2,
        }),
        catchError(err => {
          // report the error to Sentry since it failed 3 times
          this.errorHandler.handleError(err);
          // inform the modal component that the modal couldn't be loaded
          this._statusStreamSubject$.next({
            ready: true,
            errorMessage: err.message,
          });
          throw err;
        }),
        // this subject allows the user to manual retry on errors
        retryWhen(() => this.retryStreamSubject$.asObservable()),
      )
      // eslint-disable-next-line rxjs/no-ignored-subscription
      .subscribe(({ compiledModule, componentType }) => {
        /**
         * Dynamically create the provided component using the loaded module.
         *
         * Note: we need both the componentType and module here.
         *  - The module is needed for its providers.
         *  - The componentType is needed so we have something to generate
         */

        // if the modal has changed (most likely closed), don't inject the old component
        if (this.openModalRef !== modalRef) {
          return;
        }

        // inform modal component that loading succeeded
        this._statusStreamSubject$.next({ ready: true });
        // create componentFactory from compiled module
        const componentFactory = compiledModule
          .create(this.injector) // ngModuleRef
          // eslint-disable-next-line deprecation/deprecation
          .componentFactoryResolver.resolveComponentFactory(componentType);
        // create custom injector to pass in the ModalRef
        const injectionTokens = new WeakMap();
        injectionTokens.set(ModalRef, modalRef);
        const customInjector = new PortalInjector(
          this.injector,
          injectionTokens,
        );

        // create component
        // eslint-disable-next-line deprecation/deprecation
        const componentRef = this.viewContainerRef.createComponent(
          componentFactory,
          0,
          customInjector,
        );
        if (modalConfig.inputs) {
          Object.entries(modalConfig.inputs).forEach(([key, value]) => {
            // FIXME: T267853 - ComponentType<T> has no index signature.
            (componentRef as any).instance[key] = value;
          });
        }
        modalRef.set(componentRef);
      });
    return modalRef;
  }

  retryOpen() {
    this.retryStreamSubject$.next(undefined);
  }

  setContainer(viewContainerRef: ViewContainerRef, injector: Injector) {
    this.viewContainerRef = viewContainerRef;
    this.injector = injector;
  }

  getCurrentConfig() {
    return this.modalConfig;
  }

  setTitleTemplate(template?: TemplateRef<any>) {
    this._titleStreamSubject$.next(template);
  }

  private async getModalNgModule(
    component: ModalName,
  ): Promise<{ modalModule: any; componentType: ComponentType<any> }> {
    if (!this.allModals) {
      // merge the `multi` providers into one map.
      this.allModals = this.providedModals.reduce(
        (acc, routes) => ({ ...acc, ...routes }),
        {},
      );
    }

    if (!this.allModals[component]) {
      throw new Error(`missing modal module configuration for ${component}`);
    }

    return {
      modalModule: await this.allModals[component].loadModule(),
      componentType: await this.allModals[component].loadComponent(),
    };
  }
}
